import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { RichTextViewer } from '@prosolve/common-components-ui';
import { FaVolumeUp, FaPause } from 'react-icons/fa';
import AnswerText from './AnwerTypes/AnswerText';
import AnswerOptions from './AnwerTypes/AnswerOptions';
import AnswerMultiTextOptions from './AnwerTypes/AnswerMultiTextOptions';
import AnswerStars from './AnwerTypes/AnswerStars';
import AnswerFileUpload from './AnwerTypes/AnswerFileUpload';
import Dots from './Dots';
import useIsMobileView from "../../hooks/useIsMobileView";
import SoefiaQuestionAnswerPanel from './SoefiaQuestionAnswerPanel/SoefiaQuestionAnswerPanel';

// Quill's snow stylesheet hard-codes font-size: 13px on .ql-container and
// text-align: left on .ql-editor, which beat anything set/inherited from this
// wrapper. Override both so the viewer follows the wrapper's size and centering.
const QuestionDescriptionWrapper = styled.div`
  text-align: center;

  .ql-container.ql-snow,
  .ql-editor {
    font-size: inherit;
    text-align: inherit;
  }
`

export default (props) => {
  const {
    questionObj = {},
    categoryInfoMap = {},
    answers,
    setAnswers,
    peformAutoNextAction,
    largeFonts,
    extraLargeFonts,
    reverseOptions,
    isVerticalScrollMode,
    autoPlayAudio,
    answersValidationMarks,
    requiredStudentTotal
  } = props;

  const isMobileView = useIsMobileView();

  const {
    id: questionId,
    category = '',
    question = '',
    questionDescription,
    image,
    type,
    validationRegex,
    validationNote,
    starLabels,
    totalStars,
    isRow,
    isColumn,
    isSingleLineText,
    answerOptions,
    textToSpeechAudioUrl
  } = questionObj

  const audioRef = useRef();

  const [isAudioPlaying, setIsAudioPlaying] = useState(false)

  useEffect(() => {
    const audioEl = audioRef.current;
    if (autoPlayAudio && audioEl) {
      // May be rejected by browser autoplay policy if the user hasn't interacted with the page yet (e.g. hideIntro)
      audioEl.play().catch(() => {})
    }
    return () => {
      if (audioEl) {
        audioEl.pause()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onToggleAudio = () => {
    const audioEl = audioRef.current;
    if (!audioEl) {
      return;
    }
    if (audioEl.paused) {
      audioEl.play().catch(() => {})
    } else {
      audioEl.pause()
    }
  }

  const catInfo = categoryInfoMap[category] || [];

  const qAndCatFontSize = extraLargeFonts ? '48px' : largeFonts ? '36px' : '24px'

  if (type === 'soefia') {
    return <SoefiaQuestionAnswerPanel {...props} />
  }

  const hasInvalidMark = answersValidationMarks && answersValidationMarks[questionId]

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      paddingTop: '40px'
    }}>
      {
        category &&
        <div style={{ fontSize: qAndCatFontSize, fontWeight: (extraLargeFonts || largeFonts) ? 'bold' : 'bold', textAlign: 'center', marginBottom: '40px' }}>
        {category}
        </div>
      }
      {
        Boolean(questionDescription) &&
        <QuestionDescriptionWrapper style={{
          width: isMobileView ? '90%' : 'min(700px, 80%)',
          marginBottom: '20px',
          fontSize: extraLargeFonts ? '24px' : largeFonts ? '20px' : '16px'
        }}>
          <RichTextViewer value={questionDescription} />
        </QuestionDescriptionWrapper>
      }
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        columnGap: '15px',
        marginBottom: (extraLargeFonts || largeFonts) ? '40px' : '20px'
      }}>
        <div style={{ fontSize: qAndCatFontSize, fontWeight: (extraLargeFonts || largeFonts) ? 'bold' : 'bold', whiteSpace: 'pre-wrap', textAlign: 'center' }}>
          {question}
        </div>
        {
          Boolean(textToSpeechAudioUrl) &&
          <>
            {
              isAudioPlaying
                ?
                <FaPause
                  onClick={onToggleAudio}
                  style={{ fontSize: qAndCatFontSize, color: '#0280c6', cursor: 'pointer', flexShrink: 0 }}
                />
                :
                <FaVolumeUp
                  onClick={onToggleAudio}
                  style={{ fontSize: qAndCatFontSize, color: '#0280c6', cursor: 'pointer', flexShrink: 0 }}
                />
            }
            <audio
              ref={audioRef}
              src={textToSpeechAudioUrl}
              onPlay={() => setIsAudioPlaying(true)}
              onPause={() => setIsAudioPlaying(false)}
            />
          </>
        }
      </div>
      {
        image &&
          <img
            src={image}
            alt="question"
            style={{
              height: '200px',
              marginBottom: '20px'
            }}
          />
      }
      {
        !isVerticalScrollMode && category && catInfo && catInfo.length > 0 &&
        <div style={{ marginBottom: '20px' }}>
          <Dots total={catInfo.length} selectedIndex={catInfo.indexOf(questionId)} />
        </div>
      }
      <div style={{
        maxWidth: isMobileView ? '78%' : '50%',
        minWidth: isMobileView ? '78%' : '500px'
      }}>
        {
          type === 'text'
            ?
            <AnswerText
              questionId={questionId}
              answers={answers}
              setAnswers={setAnswers}
              validationRegex={validationRegex}
              validationNote={validationNote}
              isSingleLineText={isSingleLineText}
              hasInvalidMark={hasInvalidMark}
            />
            :
            (type === 'checkbox' || type === 'radio')
              ?
              <AnswerOptions
                questionId={questionId}
                type={type}
                answers={answers}
                setAnswers={setAnswers}
                options={answerOptions}
                peformAutoNextAction={peformAutoNextAction}
                isRow={isRow}
                largeFonts={largeFonts}
                extraLargeFonts={extraLargeFonts}
                reverseOptions={reverseOptions}
                hasInvalidMark={hasInvalidMark}
              />
              :
              type === 'multitext'
              ?
              <AnswerMultiTextOptions
                questionId={questionId}
                answers={answers}
                setAnswers={setAnswers}
                options={answerOptions}
                peformAutoNextAction={peformAutoNextAction}
                isRow={isRow}
                isColumn={isColumn}
                largeFonts={largeFonts}
                extraLargeFonts={extraLargeFonts}
                hasInvalidMark={hasInvalidMark}
                requiredStudentTotal={requiredStudentTotal}
              />
              :
              type === 'stars' ?
              <AnswerStars
                questionId={questionId}
                answers={answers}
                setAnswers={setAnswers}
                starLabels={starLabels}
                totalStars={totalStars}
                hasInvalidMark={hasInvalidMark}
              />
              :
              type === 'fileupload' ?
              <AnswerFileUpload
                questionId={questionId}
                answers={answers}
                setAnswers={setAnswers}
                hasInvalidMark={hasInvalidMark}
              />
              :
              <></>
        }
      </div>
    </div>
  )
}